import { UNSPECIFIED_SOLVENT } from "./updateIconNMR.js";
import { assert } from "./util.js";
function collectFormData(form) {
    const experimentsDiv = form.document.querySelector(`#${form.id}`);
    if (!experimentsDiv) {
        return null;
    }
    const activeTab = experimentsDiv.querySelector('.tab-content.active');
    const instrumentId = activeTab?.dataset.instrument;
    if (!instrumentId)
        return null;
    const formData = {
        instrument: instrumentId,
        experiments: [],
    };
    const experiments = activeTab.querySelectorAll('.experiment-item');
    for (const exp of experiments) {
        const checkbox = exp.querySelector('.experiment-toggle');
        assert(checkbox, 'Experiment toggle not found');
        const experimentData = {
            name: checkbox.dataset.experiment,
            enabled: checkbox.checked,
            parameters: {},
        };
        for (const input of exp.querySelectorAll('.parameter-input')) {
            if (input.dataset.label) {
                experimentData.parameters[input.dataset.label] = input.value;
            }
        }
        formData.experiments.push(experimentData);
    }
    return formData;
}
function createExperimentElement(document, experiment) {
    const div = document.createElement('div');
    div.className = 'experiment-item';
    const header = document.createElement('div');
    header.className = 'experiment-header';
    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.className = 'experiment-toggle';
    checkbox.dataset.experiment = experiment.experiment;
    checkbox.checked = experiment.enabled || false;
    const label = document.createElement('label');
    label.textContent = experiment.experiment;
    label.style.cursor = 'pointer';
    header.append(checkbox);
    header.append(label);
    const paramsDiv = document.createElement('div');
    paramsDiv.className = 'experiment-parameters';
    paramsDiv.style.display = experiment.enabled || false ? 'block' : 'none';
    for (const param of experiment.parameters) {
        const paramDiv = document.createElement('div');
        paramDiv.className = 'parameter-item';
        const paramLabel = document.createElement('label');
        paramLabel.textContent = param.label;
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'parameter-input';
        input.dataset.label = param.label;
        input.value = param.value;
        paramDiv.append(paramLabel);
        paramDiv.append(input);
        paramsDiv.append(paramDiv);
    }
    div.append(header);
    div.append(paramsDiv);
    return div;
}
function createTabContent(form, instrumentId, instrumentData) {
    const content = form.document.createElement('div');
    content.className = 'tab-content';
    content.dataset.instrument = instrumentId;
    for (const experiment of instrumentData.experiments) {
        content.append(createExperimentElement(form.document, experiment));
    }
    return content;
}
function switchTab(form, instrumentId) {
    const experimentsDiv = form.document.querySelector(`#${form.id}`);
    assert(experimentsDiv, 'Experiments div not found in the form');
    for (const tab of experimentsDiv.querySelectorAll('.tab')) {
        tab.classList.toggle('active', tab.dataset.instrument === instrumentId);
    }
    for (const content of experimentsDiv.querySelectorAll('.tab-content')) {
        content.classList.toggle('active', content.dataset.instrument === instrumentId);
    }
    form.onFormChange(collectFormData(form));
}
function fillSettings(form, config) {
    const experimentsDiv = form.document.querySelector(`#${form.id}`);
    assert(experimentsDiv, 'Experiments div not found in the form');
    // Used by updateIconNMR to map the selected solvent key to the IconNMR name.
    form.solventMapping = config.solventMapping;
    const holderInput = experimentsDiv.querySelector('.holder-input');
    if (holderInput) {
        holderInput.value = String(form.iconNMROptions.holder);
    }
    const deleteToggle = experimentsDiv.querySelector('.delete-existing-holder-toggle');
    if (deleteToggle) {
        deleteToggle.checked = form.iconNMROptions.deleteExistingHolder;
    }
    const autoImportToggle = experimentsDiv.querySelector('.auto-import-toggle');
    if (autoImportToggle) {
        autoImportToggle.checked = form.iconNMROptions.autoSubmit;
    }
    const solventSelect = experimentsDiv.querySelector('.solvent-select');
    if (solventSelect) {
        // Options are the user-facing keys of solventMapping, plus an unspecified
        // "-" entry. The generator maps the selected key to the IconNMR/Bruker value
        // (or the default solvent for "-") when building the file.
        solventSelect.innerHTML = '';
        const unspecifiedOption = form.document.createElement('option');
        unspecifiedOption.value = UNSPECIFIED_SOLVENT;
        unspecifiedOption.textContent = UNSPECIFIED_SOLVENT;
        solventSelect.append(unspecifiedOption);
        const solventKeys = Object.keys(config.solventMapping);
        for (const key of solventKeys) {
            const option = form.document.createElement('option');
            option.value = key;
            option.textContent = key;
            solventSelect.append(option);
        }
        // The current solvent (e.g. coming from the request) is already a key.
        // Select it directly, falling back to "unspecified" if it is unknown.
        const selectedKey = solventKeys.includes(form.iconNMROptions.solvent)
            ? form.iconNMROptions.solvent
            : UNSPECIFIED_SOLVENT;
        solventSelect.value = selectedKey;
        form.iconNMROptions.solvent = selectedKey;
    }
}
export function fillForm(form, config) {
    const experimentsDiv = form.document.querySelector(`#${form.id}`);
    assert(experimentsDiv, 'Experiments div not found in the form');
    const tabsContainer = experimentsDiv.querySelector('#tabs');
    const formContainer = experimentsDiv.querySelector('#form-container');
    assert(tabsContainer, 'Tabs container not found in the form');
    assert(formContainer, 'Form container not found in the form');
    // Clear existing content
    tabsContainer.innerHTML = '';
    formContainer.innerHTML = '';
    fillSettings(form, config);
    const instrumentIds = Object.keys(config.instruments);
    for (const [index, instrumentId] of instrumentIds.entries()) {
        const tab = form.document.createElement('button');
        tab.className = 'tab';
        tab.dataset.instrument = instrumentId;
        tab.textContent = instrumentId;
        if (index === 0)
            tab.classList.add('active');
        tabsContainer.append(tab);
        const content = createTabContent(form, instrumentId, config.instruments[instrumentId]);
        if (index === 0)
            content.classList.add('active');
        formContainer.append(content);
    }
    // Event delegation at experiments div level
    experimentsDiv.addEventListener('click', (e) => {
        const target = e.target;
        const currentTarget = e.currentTarget;
        if (target.classList.contains('tab') && target.dataset.instrument) {
            switchTab(form, target.dataset.instrument);
        }
        else if (target.classList.contains('experiment-header') ||
            target.closest('.experiment-header label')) {
            const checkbox = currentTarget
                .querySelector('.experiment-item')
                ?.querySelector('.experiment-toggle');
            if (checkbox &&
                (target.tagName === 'LABEL' || target.closest('.experiment-header'))) {
                const experimentItem = target.closest('.experiment-item');
                if (experimentItem) {
                    const checkboxInItem = experimentItem.querySelector('.experiment-toggle');
                    if (checkboxInItem) {
                        checkboxInItem.click();
                    }
                }
            }
        }
    });
    experimentsDiv.addEventListener('change', (e) => {
        const target = e.target;
        assert(target, 'Target is not an HTMLDivElement');
        if (target.classList.contains('experiment-toggle')) {
            const experimentItem = target.closest('.experiment-item');
            const params = experimentItem.querySelector('.experiment-parameters');
            params.style.display = target.checked ? 'block' : 'none';
            form.onFormChange(collectFormData(form));
        }
        else if (target.classList.contains('auto-import-toggle')) {
            form.iconNMROptions.autoSubmit = target.checked;
            form.onFormChange(collectFormData(form));
        }
        else if (target.classList.contains('delete-existing-holder-toggle')) {
            form.iconNMROptions.deleteExistingHolder = target.checked;
            form.onFormChange(collectFormData(form));
        }
        else if (target.classList.contains('solvent-select')) {
            // Store the selected key; it is mapped to the IconNMR name at generation.
            form.iconNMROptions.solvent = target.value;
            form.onFormChange(collectFormData(form));
        }
    });
    experimentsDiv.addEventListener('input', (e) => {
        const target = e.target;
        if (target.classList.contains('parameter-input')) {
            form.onFormChange(collectFormData(form));
        }
        else if (target.classList.contains('holder-input')) {
            const value = Number(target.value);
            form.iconNMROptions.holder = Number.isNaN(value) ? 0 : value;
            form.onFormChange(collectFormData(form));
        }
    });
    form.onFormChange(collectFormData(form));
}
//# sourceMappingURL=fillForm.js.map